import type {CompRef} from '@wix/document-services-types'
import {ReportableError} from '@wix/document-manager-utils'
import type {DataModelAPI} from '../dataModel/dataModel'
import {DATA_TYPES} from '../../constants/constants'

interface ContextListValue {
    contextType?: string
}

interface ContextList {
    values?: ContextListValue[]
}

interface ValidationErrorBaseConfig {
    errorType: string
    apiName: string
}

interface ValidationErrorConfig extends ValidationErrorBaseConfig {
    dataModel: DataModelAPI
}

const throwValidationError = (
    {errorType, apiName}: ValidationErrorBaseConfig,
    apiMethod: string,
    message: string,
    extras: Record<string, any> = {}
): never => {
    throw new ReportableError({errorType, message: `${apiName}.${apiMethod}: ${message}`, extras})
}

export const createBindingsValidators = ({errorType, apiName, dataModel}: ValidationErrorConfig) => {
    const config = {errorType, apiName}

    return {
        validateCompRef(apiMethod: string, compRef: CompRef | undefined): void {
            if (!compRef) {
                throwValidationError(config, apiMethod, 'The component reference is required', {
                    apiName,
                    apiMethod,
                    compRef
                })
            }
        },
        validateComponentExists(apiMethod: string, compRef: CompRef): void {
            if (!dataModel.getItem(compRef.id, compRef.type)) {
                throwValidationError(config, apiMethod, `Component not found`, {apiName, apiMethod, compRef})
            }
        }
    }
}

export const componentHasContextType = (compRef: CompRef, contextType: string, dataModel: DataModelAPI): boolean => {
    const contextList = dataModel.components.getItem(compRef, DATA_TYPES.contexts) as ContextList | undefined
    if (!contextList?.values?.length) {
        return false
    }

    return contextList.values.some(contextConfig => contextConfig.contextType === contextType)
}

export const hasBindingConfigMatching = <T>(bindingsList: unknown, predicate: (config: T) => boolean): boolean =>
    Boolean((bindingsList as {data?: T[]} | undefined)?.data?.some(predicate))
